import { useAgenticDocumentSearch } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useAgenticDocumentSearch'
import { useUnifiedDocumentSearch } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useUnifiedDocumentSearch'
import type {
  DocumentSearchResult,
  SearchEngine,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import {
  getDocumentSearchEngine,
  isAiDocumentSearchEngine,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchMode'
import type {
  DocumentSearchMode,
  SearchHistoryItem,
} from 'features/knowledgeDocumentViewer/types'
import { useGeckoPermissions } from 'features/permissions'
import { DocumentsViewerContentFilter } from 'routes/documents/-searchParams'
import { PERMISSION_CANTILEVER_DOC_CHAT_VIEWER } from 'types'

interface UseEnabledDocumentSearchArgs {
  siteId: string
  tabId?: string
  searchQuery: string
  contentFilter: DocumentsViewerContentFilter
  searchMode: DocumentSearchMode
  linkedMode: boolean
  browseAll: boolean
  agenticSearch?: SearchHistoryItem['searchState']['agenticSearch']
}

interface UseEnabledDocumentSearchResult {
  rawDocumentSearchData: DocumentSearchResult | undefined
  isLoadingSearch: boolean
  searchEngine: SearchEngine
  searchLatencyMs: number | undefined
  refreshDocumentSearch: () => void
}

export function useEnabledDocumentSearch({
  siteId,
  tabId,
  searchQuery,
  contentFilter,
  searchMode,
  linkedMode,
  browseAll,
  agenticSearch,
}: UseEnabledDocumentSearchArgs): UseEnabledDocumentSearchResult {
  const { hasPermissions } = useGeckoPermissions()
  const hasDocChatViewer = hasPermissions([
    PERMISSION_CANTILEVER_DOC_CHAT_VIEWER,
  ])
  const searchEngine = getDocumentSearchEngine({
    searchMode,
    contentFilter,
    isLinkedDocsMode: linkedMode,
    browseAll,
    hasDocChatViewer,
  })
  const shouldSearchDocuments =
    contentFilter === 'documents' &&
    !linkedMode &&
    !browseAll &&
    searchQuery.trim().length > 0
  const shouldUseAgenticSearch = isAiDocumentSearchEngine(searchEngine)
  const unifiedDocumentSearch = useUnifiedDocumentSearch({
    enabled: shouldSearchDocuments && !shouldUseAgenticSearch,
    siteId,
    searchQuery,
  })
  const agenticDocumentSearch = useAgenticDocumentSearch({
    enabled: shouldSearchDocuments && shouldUseAgenticSearch,
    siteId,
    tabId,
    searchQuery,
    agenticSearch,
    ...(isAiDocumentSearchEngine(searchEngine) ? { engine: searchEngine } : {}),
  })
  const activeDocumentSearch = shouldUseAgenticSearch
    ? agenticDocumentSearch
    : unifiedDocumentSearch

  return {
    ...activeDocumentSearch,
    searchEngine,
  }
}
